import { useEffect } from "react"
import { batch, useSignal } from "@preact/signals-react"
import { postSubmit } from "@ui-seeker/utils"

import { NEWS_MANAGEMENT_APIS } from "@constants"
import { isSystemBusy } from "@signals"
import { announcements, audiences, listFilters } from "../@signals"

/** Drop the untouched filters: the API reads a missing field as "no filter", and `state`
 *  is a closed set — sending an explicit `null` for it would be a 422. */
const _activeFilters = filters => Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== null && value !== "" && value !== undefined)
)

export function fetchAnnouncements() {
    return postSubmit(NEWS_MANAGEMENT_APIS.CONTEXT, _activeFilters(listFilters.value))
        .then(res => { announcements.value = res?.data ?? [] })
        .catch(() => batch(() => {
            isSystemBusy.value = true
            announcements.value = []
        }))
}

/** The listing leaves the HTML out — it is the only heavy column — so the editor asks for
 *  the announcement it is about to open. */
export function fetchAnnouncement(announcementID) {
    return postSubmit(NEWS_MANAGEMENT_APIS.DETAIL, { announcementID })
}

export function fetchAudiences() {
    return postSubmit(NEWS_MANAGEMENT_APIS.AUDIENCES, {})
        .then(res => { audiences.value = res ?? { customers: [], users: [] } })
        .catch(() => { audiences.value = { customers: [], users: [] } })
}

export default function useAnnouncements() {
    const isLoadingTable = useSignal(false)
    const isSaving = useSignal(false)

    const reload = () => {
        isLoadingTable.value = true
        return fetchAnnouncements().finally(() => { isLoadingTable.value = false })
    }

    useEffect(() => {
        isLoadingTable.value = true
        Promise.all([fetchAnnouncements(), fetchAudiences()])
            .finally(() => { isLoadingTable.value = false })
    }, [])

    /** Create when there is no id, replace when there is. `/update` is a **full
     *  replacement**, so the form always sends every field — an omitted one is written as
     *  its default, not left alone. */
    const saveAnnouncement = (payload, announcementID = null) => {
        isSaving.value = true
        const request = announcementID
            ? postSubmit(NEWS_MANAGEMENT_APIS.UPDATE, { ...payload, announcementID })
            : postSubmit(NEWS_MANAGEMENT_APIS.CREATE, payload)
        return request.then(() => reload()).finally(() => { isSaving.value = false })
    }

    const toggleActive = (announcementID, active) =>
        postSubmit(NEWS_MANAGEMENT_APIS.ENABLED, { announcementID, active }).then(() => reload())

    const removeAnnouncement = announcementID =>
        postSubmit(NEWS_MANAGEMENT_APIS.DELETE, { announcementID }).then(() => reload())

    return {
        isLoadingTable: isLoadingTable.value,
        isSaving: isSaving.value,
        reload,
        saveAnnouncement,
        toggleActive,
        removeAnnouncement,
    }
}
